import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface UpgradeOverlayProps {
  active: boolean
  children: React.ReactNode
  className?: string
  // 'chart' blurs only the chart container (lets a sibling CTA inside CardContent stay sharp);
  // 'content' blurs the entire CardContent (used when the CTA sits outside any card).
  blurTarget?: 'chart' | 'content'
  showCta?: boolean
  // Hide subtitles and badges inside card headers, leaving only the title.
  hideHeaderExtras?: boolean
}

const UpgradeOverlay = ({
  active,
  children,
  className,
  blurTarget = 'chart',
  showCta = false,
  hideHeaderExtras = false,
}: UpgradeOverlayProps) => {
  if (!active) {
    return <>{children}</>
  }

  const blurClass =
    blurTarget === 'chart'
      ? '[&_[data-slot=chart]]:blur-md'
      : '[&_[data-slot=card-content]]:blur-md'

  const headerExtrasClass = hideHeaderExtras
    ? '[&_[data-slot=card-header]_p]:hidden [&_[data-slot=card-header]_span]:hidden'
    : ''

  return (
    <div className={cn('relative', className)}>
      {showCta && (
        <div className="flex justify-center px-6 py-6 md:hidden">
          <UpgradeCta />
        </div>
      )}
      <div
        aria-hidden
        className={cn(
          'pointer-events-none select-none [&_[data-slot=card]]:overflow-hidden [&_[data-slot=card-header]]:rounded-t-xl [&_[data-slot=card-header]]:bg-muted',
          blurClass,
          headerExtrasClass
        )}
      >
        {children}
      </div>
      {showCta && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 top-[60px] hidden items-center justify-center px-6 md:flex">
          <UpgradeCta />
        </div>
      )}
    </div>
  )
}

export const UpgradeCta = () => {
  const navigate = useNavigate()
  return (
    <div className="pointer-events-auto flex w-[610px] max-w-full flex-col items-center gap-5 text-center">
      <div
        className="text-[40px] font-normal leading-[1] text-foreground xl:text-[72px] xl:leading-[72px]"
        style={{ fontFamily: 'EBGaramond, serif' }}
      >
        Upgrade to Enterprise
      </div>
      <p className="text-[18px] font-normal leading-7 text-foreground">
        Unlock additional usage data, team management tools, and security permissions across your organization.
      </p>
      <Button
        onClick={() => navigate('/app/settings/billing')}
        className="w-[154px] text-[15px] font-semibold leading-5"
      >
        See plans
      </Button>
    </div>
  )
}

export default UpgradeOverlay
